import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { api } from '../lib/api';
import { flag } from '../config';
import { clearForgotPasswordEmail, emailFromSearch, forgotPasswordEmail } from '../components/site/auth/authLinks';
import lazyAccountPage from '../components/site/auth/lazyAccountPage';

/**
 * `?email=` prefills the field: the "Set a password" links in the claim, wallet and
 * venue-approval emails point here with the address (the claim success box and the wallet
 * page, slot model only, pass it in router state, which the redesigned page reads).
 * (Defined in components/site/auth/authLinks, shared with the redesigned page.)
 */
export { emailFromSearch };

// SLOT_MODEL on: the redesigned page (site redesign Phase 6), loaded on demand.
const ForgotPasswordSite = lazyAccountPage(
  'ForgotPasswordSite',
  () => import(/* webpackChunkName: "account-pages" */ './account/ForgotPasswordSite'),
);

/** /forgot-password: the redesigned page with SLOT_MODEL on, the legacy V5 page (unchanged) off. */
export default function ForgotPassword() {
  return flag('SLOT_MODEL') ? <ForgotPasswordSite /> : <ForgotPasswordLegacy />;
}

const ForgotPasswordLegacy = () => {
  const location = useLocation();
  // `?email=`, or the address kept for this page (index.html guard / in-app handoff).
  const [email, setEmail] = useState(() => forgotPasswordEmail(location));
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      clearForgotPasswordEmail();
      setSubmitted(true);
    } catch (err) {
      // Always show success to prevent email enumeration
      clearForgotPasswordEmail();
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#FEF7ED' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Back to Home Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 mb-8 transition-colors"
          style={{ color: 'rgba(30, 27, 75, 0.7)' }}
        >
          <ArrowLeft size={20} />
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>Back to Home</span>
        </Link>

        {/* V5 Card */}
        <div
          className="rounded-xl overflow-hidden"
          style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid rgba(30, 27, 75, 0.05)',
            boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)',
            borderLeft: '4px solid #8b5cf6'
          }}
        >
          {!submitted ? (
            <div className="p-8">
              {/* Header */}
              <div className="text-center mb-8">
                {/* Code accent */}
                <p
                  className="mb-2"
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '12px',
                    color: 'rgba(30, 27, 75, 0.3)'
                  }}
                >
                  // forgot_password
                </p>

                {/* Icon */}
                <div
                  className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
                >
                  <Mail size={28} style={{ color: '#8b5cf6' }} />
                </div>

                {/* Title */}
                <h1
                  className="mb-2"
                  style={{
                    fontFamily: "'Faustina', Georgia, serif",
                    fontSize: '32px',
                    fontWeight: 500,
                    color: '#1e1b4b'
                  }}
                >
                  Forgot Password?
                </h1>
                <p
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '14px',
                    color: 'rgba(30, 27, 75, 0.5)'
                  }}
                >
                  No worries! Enter your email and we'll send you a reset link.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="forgot-email"
                    className="block mb-2"
                    style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'rgba(30, 27, 75, 0.7)'
                    }}
                  >
                    Email Address
                  </label>
                  <Input
                    id="forgot-email"
                    autoComplete="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full focus:ring-0"
                    style={{
                      height: '48px',
                      padding: '0 16px',
                      backgroundColor: '#FFFFFF',
                      border: '2px solid rgba(30, 27, 75, 0.1)',
                      borderRadius: '12px',
                      fontSize: '15px',
                      fontFamily: "'DM Sans', sans-serif"
                    }}
                  />
                </div>

                {error && (
                  <p
                    className="text-sm"
                    style={{ color: '#ef4444', fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {error}
                  </p>
                )}

                {/* V5 Primary Button - Purple Gradient */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full font-semibold transition-all hover:-translate-y-0.5"
                  style={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: '#FFFFFF',
                    height: '48px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 600,
                    fontFamily: "'DM Sans', sans-serif",
                    boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4), 0 0 30px rgba(139, 92, 246, 0.2)'
                  }}
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={20} />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </form>

              {/* Back to Login */}
              <div className="text-center mt-6">
                <Link
                  to="/"
                  className="text-sm hover:underline transition-colors"
                  style={{
                    color: '#8b5cf6',
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 500
                  }}
                >
                  Back to Login
                </Link>
              </div>
            </div>
          ) : (
            /* Success State */
            <div className="p-8 text-center">
              {/* Success Icon */}
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}
              >
                <CheckCircle size={28} style={{ color: '#22c55e' }} />
              </div>

              <h2
                className="mb-2"
                style={{
                  fontFamily: "'Faustina', Georgia, serif",
                  fontSize: '24px',
                  fontWeight: 500,
                  color: '#1e1b4b'
                }}
              >
                Check Your Email
              </h2>
              <p
                className="mb-6"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '14px',
                  color: 'rgba(30, 27, 75, 0.5)'
                }}
              >
                If an account exists for <strong style={{ color: '#1e1b4b' }}>{email}</strong>, you'll receive a password reset link shortly.
              </p>
              <p
                className="text-sm mb-6"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  color: 'rgba(30, 27, 75, 0.4)'
                }}
              >
                Don't see the email? Check your spam folder or try again in a few minutes.
              </p>
              <Link to="/">
                <Button
                  className="font-semibold transition-all hover:-translate-y-0.5"
                  style={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: '#FFFFFF',
                    padding: '12px 32px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 600,
                    fontFamily: "'DM Sans', sans-serif",
                    boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4), 0 0 30px rgba(139, 92, 246, 0.2)'
                  }}
                >
                  Back to Home
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Footer */}
        <p
          className="text-center mt-6"
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '14px',
            color: 'rgba(30, 27, 75, 0.5)'
          }}
        >
          Remember your password?{' '}
          <Link
            to="/"
            className="font-semibold hover:underline"
            style={{ color: '#8b5cf6' }}
          >
            Sign In
          </Link>
        </p>
      </motion.div>
    </div>
  );
};

